interface ISortableById {
  id?: number;
  name?: string;
}

interface DataPoint {
  x: number;
  y: number;
}

// Sorts object in an array based on id and returns a shallow clone.
// Note: If the object doesn't contain an id than it will not be sorted.
// id should be a number or a string convertable to a number
export const getSortedItemsBasedOnId = (data: ISortableById[] = []) => {
  return [...data].sort((a: ISortableById, b: ISortableById) => {
    if (!a.id || !b.id) return 0;
    if (Number(a.id) - Number(b.id) > 0) {
      return 1;
    } else if (Number(a.id) - Number(b.id) < 0) {
      return -1;
    }
    return 0;
  });
};

// returns a debounced function with a specified delay
export function debounce(func: any, delay = 300) {
  let timer: ReturnType<typeof setTimeout>;
  return function (...args: any) {
    clearTimeout(timer);
    timer = setTimeout(() => {
      func(...args);
    }, delay);
  };
}

export function parseIntOrReturnString(str: string | number) {
  const parsedNumber = parseInt(`${str}`);
  if (isNaN(parsedNumber)) {
    return str;
  } else {
    return parsedNumber;
  }
}

export function parsePhoneNumber(phoneNumber: string) {
  const cleaned = ('' + phoneNumber).replace(/\D/g, '');
  const match = cleaned.match(/^(\d{3})(\d{7})$/);
  if (match) {
    return {
      areaCode: match[1],
      phoneNumber: match[2],
    };
  }

  return null;
}

/**
 * Calculates the linear regression (best fit line) for a set of data points.
 * Uses the least squares method to minimize the sum of squared residuals.
 *
 * Formula: y = mx + b
 * where: m = slope
 *        b = y-intercept
 *
 */
export function linearRegression(data: DataPoint[]) {
  const n = data.length;

  let sumX = 0; // Σx (sum of x values)
  let sumY = 0; // Σy (sum of y values)
  let sumXY = 0; // Σ(x*y) (sum of products)
  let sumXX = 0; // Σ(x²) (sum of squared x values)

  for (const point of data) {
    sumX += point.x;
    sumY += point.y;
    sumXY += point.x * point.y;
    sumXX += point.x * point.x;
  }

  // Calculate slope (m) using the formula:
  // m = (n*Σ(xy) - Σx*Σy) / (n*Σ(x²) - (Σx)²)
  const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);

  // Calculate y-intercept (b) using the formula:
  // b = (Σy - m*Σx) / n
  const intercept = (sumY - slope * sumX) / n;

  return { slope, intercept };
}
